/**
 * AdminBankMatchingPool.jsx — Banka hareketi eşleşme havuzu.
 * Route: /admin/bank-matching-pool · Roller: admin, superadmin (bkz. App.js RoleRoute).
 *
 * Bağlam: havale ile gelen para banka hareketi olarak sisteme düşer (CSV import /
 * manuel giriş — bkz. backend routes/bank_transactions.py; ileride banka API
 * connector'ları services/bank_connectors/). Admin bu ekranda hareketi öğrencinin
 * bekleyen ödemesine (payment_id, status=pending, provider=bank_transfer) eşleştirir.
 * Eşleştirme backend'de payment'ı completed yapar, enrollment'ı aktive eder ve
 * faturayı (iç kayıt — resmi Paraşüt faturası DEĞİL) günceller; Paraşüt'e fatura
 * kuyruklama işini backend kendisi yapar (bu ekranın kapsamı DIŞINDA).
 *
 * "Otomatik Eşleştir" düğmesi services/matching_engine.py'yi manuel tetikler:
 * confidence=100 (referans+tutar tam) kayıtları KENDİSİ onaylar (otomatik eşleşenler
 * bu listede artık görünmez — unmatched'tan çıkar), confidence<100 kayıtlara ise
 * `suggested_payment_id`/`match_confidence` yazar — bu ekran o öneriyi gösterir.
 *
 * Tur 2 (2026-09-29, feature/parasut-fatura-faz4-web) — "Eşleşenler" sekmesi
 * EKLENDİ (`useMatchedBankTransactions`). Tur 3'te backend (routes/
 * bank_transactions.py, salt okundu) HAZIR olarak doğrulandı — kontratla
 * birebir eşleşiyor. Öneri zenginleştirmesi (öğrenci adı/kurs/tutar/vade) ve
 * "Elle Seç" için `pending-payments` ucu da Tur 3'te backend'de HAZIR
 * doğrulandı (bkz. BankMatchingCard.jsx + api/mutations/bankMatching.js /
 * api/queries/students.js docstring'leri).
 *
 * Tur 4 (elle test bulguları) — "Eşleşenler" satırlarında `matched_at`
 * (ISO zaman damgası) artık `fmtIstanbulDateTime` ile Europe/Istanbul yerel
 * saatine, `date` (bare "YYYY-MM-DD") `fmtDateOnly` ile gg.aa.yyyy'e
 * çevriliyor; `installment_no` VARSA kurs adının yanında gösteriliyor
 * (backend eklerse otomatik görünür, eklemezse sessizce atlanır).
 * `BankMatchingCard`'a `banks` prop'u geçirildi (banka adı geri düşüşü için,
 * bkz. o dosyanın docstring'i).
 */
import { useState } from 'react';
import AdminLayout from '../../components/admin/AdminLayout';
import { PageHeader, EmptyState, LoadingState, Pagination, ConfirmDialog, StatusBadge } from '../../components/admin';
import BankMatchingCard from '../../components/admin/bankMatching/BankMatchingCard';
import { Button } from '../../components/ui/button';
import { useLanguage } from '../../contexts/LanguageContext';
import { useToast } from '../../contexts/ToastContext';
import { fmtTRY } from '../../utils/formatCurrency';
import { getApiErrorMessage } from '../../utils/apiError';
import {
  useUnmatchedBankTransactions,
  useTriggerAutoMatch,
  useMatchedBankTransactions,
} from '../../api/mutations/bankMatching';
import { useAccountingBanks } from '../../api/queries/accounting';
import { fmtDateOnly } from '../../utils/formatDateOnly';
import { fmtIstanbulDateTime } from '../../utils/formatIstanbulDateTime';
import { AlertTriangle, Inbox, RefreshCw, Sparkles, CheckCheck } from 'lucide-react';

const LIMIT = 20;

// ── "Havuz" (unmatched) görünümü ────────────────────────────────────────────
function UnmatchedPoolView({ t, lang }) {
  const toast = useToast();
  const [bankId, setBankId] = useState('');
  const [direction, setDirection] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [page, setPage] = useState(1);
  const [autoMatchConfirmOpen, setAutoMatchConfirmOpen] = useState(false);

  const skip = (page - 1) * LIMIT;
  const { data, isLoading, isError, isFetching, refetch } = useUnmatchedBankTransactions({
    bankId: bankId || undefined,
    direction: direction || undefined,
    dateFrom: dateFrom || undefined,
    dateTo: dateTo || undefined,
    skip,
    limit: LIMIT,
  });
  const { data: banks = [] } = useAccountingBanks();
  const autoMatch = useTriggerAutoMatch();

  const items = data?.items || [];
  const total = data?.total || 0;
  const totalPages = Math.max(1, Math.ceil(total / LIMIT));

  const runAutoMatch = async () => {
    try {
      const res = await autoMatch.mutateAsync();
      toast.success(
        `${t('admin.bank_match_auto_success')} — ${t('admin.bank_match_auto_approved')}: ${res.auto_approved}, `
        + `${t('admin.bank_match_auto_suggestions')}: ${res.suggestions_added}`
      );
      setAutoMatchConfirmOpen(false);
    } catch (err) {
      toast.error(getApiErrorMessage(err, lang));
      setAutoMatchConfirmOpen(false);
    }
  };

  return (
    <>
      <div className="mb-4 flex justify-end">
        <Button
          type="button"
          onClick={() => setAutoMatchConfirmOpen(true)}
          disabled={autoMatch.isPending}
          data-testid="bank-match-auto-trigger-btn"
          className="inline-flex items-center gap-1.5"
        >
          <Sparkles size={15} />
          {t('admin.bank_match_auto_button')}
        </Button>
      </div>

      <div className="mb-4 grid grid-cols-1 sm:grid-cols-4 gap-3">
        <div>
          <label htmlFor="bank-match-bank-filter" className="text-xs font-bold block mb-1">
            {t('admin.bank_match_filter_bank')}
          </label>
          <select
            id="bank-match-bank-filter"
            value={bankId}
            onChange={(e) => { setBankId(e.target.value); setPage(1); }}
            data-testid="bank-match-bank-filter"
            className="px-3 py-2 rounded-xl border-2 border-[var(--border-light)] bg-white font-semibold text-sm w-full"
          >
            <option value="">{t('admin.bank_match_filter_all')}</option>
            {banks.map((b) => <option key={b.bank_id} value={b.bank_id}>{b.name}</option>)}
          </select>
        </div>
        <div>
          <label htmlFor="bank-match-direction-filter" className="text-xs font-bold block mb-1">
            {t('admin.bank_match_filter_direction')}
          </label>
          <select
            id="bank-match-direction-filter"
            value={direction}
            onChange={(e) => { setDirection(e.target.value); setPage(1); }}
            data-testid="bank-match-direction-filter"
            className="px-3 py-2 rounded-xl border-2 border-[var(--border-light)] bg-white font-semibold text-sm w-full"
          >
            <option value="">{t('admin.bank_match_filter_all')}</option>
            <option value="in">{t('admin.bank_match_direction_in')}</option>
            <option value="out">{t('admin.bank_match_direction_out')}</option>
          </select>
        </div>
        <div>
          <label htmlFor="bank-match-date-from" className="text-xs font-bold block mb-1">
            {t('admin.bank_match_filter_date_from')}
          </label>
          <input
            id="bank-match-date-from"
            type="date"
            value={dateFrom}
            onChange={(e) => { setDateFrom(e.target.value); setPage(1); }}
            data-testid="bank-match-date-from"
            className="px-3 py-2 rounded-xl border-2 border-[var(--border-light)] bg-white font-semibold text-sm w-full"
          />
        </div>
        <div>
          <label htmlFor="bank-match-date-to" className="text-xs font-bold block mb-1">
            {t('admin.bank_match_filter_date_to')}
          </label>
          <input
            id="bank-match-date-to"
            type="date"
            value={dateTo}
            onChange={(e) => { setDateTo(e.target.value); setPage(1); }}
            data-testid="bank-match-date-to"
            className="px-3 py-2 rounded-xl border-2 border-[var(--border-light)] bg-white font-semibold text-sm w-full"
          />
        </div>
      </div>

      {isLoading ? (
        <LoadingState variant="cards" count={4} />
      ) : isError ? (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-destructive/40 bg-destructive/5 p-4">
          <div className="flex items-center gap-2 text-sm text-destructive">
            <AlertTriangle size={18} className="shrink-0" aria-hidden="true" />
            <div>
              <p className="font-semibold">{t('admin.bank_match_error_title')}</p>
              <p>{t('admin.bank_match_error_desc')}</p>
            </div>
          </div>
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => refetch()}
            disabled={isFetching}
          >
            <RefreshCw size={14} className="mr-1" aria-hidden="true" />
            {t('admin.bank_match_error_retry')}
          </Button>
        </div>
      ) : items.length === 0 ? (
        <EmptyState
          icon={<Inbox size={40} strokeWidth={1.5} />}
          title={t('admin.bank_match_empty_title')}
          description={t('admin.bank_match_empty_desc')}
        />
      ) : (
        <div className="flex flex-col gap-3" data-testid="bank-match-list">
          {items.map((item) => (
            <BankMatchingCard key={item.transaction_id} item={item} t={t} lang={lang} banks={banks} />
          ))}
        </div>
      )}

      <Pagination
        page={page}
        totalPages={totalPages}
        onPageChange={setPage}
        showInfo
        from={total === 0 ? 0 : skip + 1}
        to={Math.min(skip + LIMIT, total)}
        total={total}
        loading={isLoading}
      />

      <ConfirmDialog
        open={autoMatchConfirmOpen}
        onOpenChange={(o) => { if (!o) setAutoMatchConfirmOpen(false); }}
        title={t('admin.bank_match_auto_confirm_title')}
        description={t('admin.bank_match_auto_confirm_desc')}
        variant="info"
        confirmLabel={t('admin.bank_match_auto_button')}
        onConfirm={runAutoMatch}
        loading={autoMatch.isPending}
      />
    </>
  );
}

// ── "Eşleşenler" (matched) görünümü ─────────────────────────────────────────
// Backend HAZIR (Tur 3'te doğrulandı, bkz. useMatchedBankTransactions
// docstring'i). `isError` dalı savunma amaçlı korunur (gerçek ağ/sunucu
// hatasında hata durumu gösterir, sayfa ÇÖKMEZ).
function MatchedListView({ t, lang }) {
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [page, setPage] = useState(1);

  const skip = (page - 1) * LIMIT;
  const { data, isLoading, isError, isFetching, refetch } = useMatchedBankTransactions({
    dateFrom: dateFrom || undefined,
    dateTo: dateTo || undefined,
    skip,
    limit: LIMIT,
  });

  const items = data?.items || [];
  const total = data?.total || 0;
  const totalPages = Math.max(1, Math.ceil(total / LIMIT));

  return (
    <>
      <div className="mb-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label htmlFor="bank-matched-date-from" className="text-xs font-bold block mb-1">
            {t('admin.bank_match_filter_date_from')}
          </label>
          <input
            id="bank-matched-date-from"
            type="date"
            value={dateFrom}
            onChange={(e) => { setDateFrom(e.target.value); setPage(1); }}
            data-testid="bank-matched-date-from"
            className="px-3 py-2 rounded-xl border-2 border-[var(--border-light)] bg-white font-semibold text-sm w-full"
          />
        </div>
        <div>
          <label htmlFor="bank-matched-date-to" className="text-xs font-bold block mb-1">
            {t('admin.bank_match_filter_date_to')}
          </label>
          <input
            id="bank-matched-date-to"
            type="date"
            value={dateTo}
            onChange={(e) => { setDateTo(e.target.value); setPage(1); }}
            data-testid="bank-matched-date-to"
            className="px-3 py-2 rounded-xl border-2 border-[var(--border-light)] bg-white font-semibold text-sm w-full"
          />
        </div>
      </div>

      {isLoading ? (
        <LoadingState variant="cards" count={4} />
      ) : isError ? (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-destructive/40 bg-destructive/5 p-4">
          <div className="flex items-center gap-2 text-sm text-destructive">
            <AlertTriangle size={18} className="shrink-0" aria-hidden="true" />
            <div>
              <p className="font-semibold">{t('admin.bank_matched_error_title')}</p>
              <p>{t('admin.bank_matched_error_desc')}</p>
            </div>
          </div>
          <Button type="button" size="sm" variant="outline" onClick={() => refetch()} disabled={isFetching}>
            <RefreshCw size={14} className="mr-1" aria-hidden="true" />
            {t('admin.bank_match_error_retry')}
          </Button>
        </div>
      ) : items.length === 0 ? (
        <EmptyState
          icon={<CheckCheck size={40} strokeWidth={1.5} />}
          title={t('admin.bank_matched_empty_title')}
          description={t('admin.bank_matched_empty_desc')}
        />
      ) : (
        <div className="flex flex-col gap-2" data-testid="bank-matched-list">
          {items.map((item) => (
            <div key={item.txn_id} className="card-clay p-3 flex flex-wrap items-center justify-between gap-2 text-xs" data-testid={`bank-matched-row-${item.txn_id}`}>
              <div className="flex flex-wrap items-center gap-3">
                <span className="font-bold text-sm">{fmtTRY(item.amount)}</span>
                <span className="text-[var(--text-muted)]">{fmtDateOnly(item.date, lang) || '—'}</span>
                <span>{item.sender_name || '—'}</span>
                {item.student_name && (
                  <StatusBadge variant="success" label={item.student_name} />
                )}
                {item.course_title && (
                  <span className="text-[var(--text-muted)]">
                    {item.course_title}
                    {item.installment_no ? ` — #${item.installment_no}` : ''}
                  </span>
                )}
              </div>
              <span className="text-[var(--text-muted)]">
                {t('admin.bank_matched_matched_at')}: {fmtIstanbulDateTime(item.matched_at, lang)}
              </span>
            </div>
          ))}
        </div>
      )}

      <Pagination
        page={page}
        totalPages={totalPages}
        onPageChange={setPage}
        showInfo
        from={total === 0 ? 0 : skip + 1}
        to={Math.min(skip + LIMIT, total)}
        total={total}
        loading={isLoading}
      />
    </>
  );
}

// ── Public ────────────────────────────────────────────────────────────────
export default function AdminBankMatchingPool() {
  const { t, lang } = useLanguage();
  const [tab, setTab] = useState('pool');

  return (
    <AdminLayout>
      <PageHeader
        breadcrumbs={[
          { label: t('admin.nav.finance'), href: '/admin/accounting' },
          { label: t('admin.nav.bankMatchingPool') },
        ]}
        title={t('admin.bank_match_title')}
        subtitle={t('admin.bank_match_subtitle')}
      />

      <div className="mb-4 flex gap-2 border-b border-[var(--border-light)]" role="tablist">
        {[
          { id: 'pool', label: t('admin.bank_match_tab_pool') },
          { id: 'matched', label: t('admin.bank_match_tab_matched') },
        ].map((tabItem) => (
          <button
            key={tabItem.id}
            type="button"
            role="tab"
            aria-selected={tab === tabItem.id}
            data-testid={`bank-match-tab-${tabItem.id}`}
            onClick={() => setTab(tabItem.id)}
            className={`px-4 py-2 text-sm font-bold ${
              tab === tabItem.id
                ? 'border-b-2 border-[var(--brand-primary)] text-[var(--brand-primary)]'
                : 'text-[var(--text-muted)]'
            }`}
          >
            {tabItem.label}
          </button>
        ))}
      </div>

      {tab === 'pool' ? <UnmatchedPoolView t={t} lang={lang} /> : <MatchedListView t={t} lang={lang} />}
    </AdminLayout>
  );
}
